import React, { useState, useEffect, useContext } from 'react'
import { toast } from "react-toastify"
import Popup from '../../../uicomponents/popup'
import FutworkButton from '../../../uicomponents/button/Button'
import FormInput from '../../../uicomponents/form-input/forminput'
import ThreeDotLoader from '../../utilities/ThreeDotLoader'
import PreviewInteractiveScript from '../Tools/PreviewInteractiveScript'
import ApiActions from '../../../actions/ApiActions'
import { ISContext } from './ISContext'

const VIEW = {
    LIST: 'list',
    PREVIEW: 'preview',
    CONFIRM: 'confirm'
}

//The list only tells us when a version was written and what it was called.
//Which languages it holds is inside the object, so it arrives with the preview.
//Labels are slugified on the way in because they become part of the object
//name; show them back the way they were typed rather than as a file name.
const displayLabel = (snapshot) => {
    if (!snapshot || !snapshot.label) return 'Unnamed snapshot'
    return snapshot.label.replace(/-/g, ' ')
}

const formatCapturedAt = (value) => {
    if (!value) return ''
    const date = new Date(value)
    return date.toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    })
}

const Snapshots = ({ show, closePopup }) => {
    const { teleproject, teleprojectdata, teleprojectTitle, unSaved, scriptPublished } = useContext(ISContext)

    const [view, setView] = useState(VIEW.LIST)
    const [snapshots, setSnapshots] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isBusy, setIsBusy] = useState(false)

    const [label, setLabel] = useState('')
    const [selected, setSelected] = useState(null)
    const [selectedScripts, setSelectedScripts] = useState(null)
    //Deliberately unset: restore is destructive, so the user states the outcome
    //rather than accepting a default one.
    const [publish, setPublish] = useState(null)
    //Cancel should go back where the confirm was opened from - the preview, or
    //the list when Restore was clicked on the row.
    const [confirmFrom, setConfirmFrom] = useState(VIEW.LIST)

    useEffect(() => {
        if (show) {
            setView(VIEW.LIST)
            setLabel('')
            setSelected(null)
            setSelectedScripts(null)
            setPublish(null)
            loadSnapshots()
        }
    }, [show])

    const loadSnapshots = async () => {
        setIsLoading(true)
        try {
            const result = await ApiActions.getSnapshots(teleproject)
            setSnapshots(Array.isArray(result) ? result : [])
        } catch (error) {
            toast('Could not load snapshots')
        }
        setIsLoading(false)
    }

    const handleCapture = async () => {
        setIsBusy(true)
        try {
            const result = await ApiActions.captureSnapshot({ teleprojectId: teleproject, label })
            //Re-listing is the confirmation that the write actually landed - a
            //toast on its own cannot tell a successful capture from a silent one.
            await loadSnapshots()
            setLabel('')
            toast(result && result.duplicate
                ? `No changes since "${displayLabel(result)}" - nothing new saved`
                : 'Snapshot captured')
        } catch (error) {
            toast('Could not capture the snapshot')
        }
        setIsBusy(false)
    }

    const loadSnapshot = async (snapshot, view) => {
        setIsBusy(true)
        try {
            const scripts = await ApiActions.getSnapshot(teleproject, snapshot.version)
            setSelected(snapshot)
            setSelectedScripts(scripts)
            setPublish(null)
            if (view === VIEW.CONFIRM) setConfirmFrom(VIEW.LIST)
            setView(view)
        } catch (error) {
            toast('Could not open this snapshot')
        }
        setIsBusy(false)
    }

    const handlePreview = (snapshot) => loadSnapshot(snapshot, VIEW.PREVIEW)

    //Restore from the row goes to the confirm screen, never straight to the
    //action. Previewing first stopped being a real gate once Open flow could
    //reach the canvas from here - the informing is done by the confirm screen,
    //which names the snapshot, states whether the script is live, shows the
    //language diff and makes the publish decision explicit.
    const openConfirm = (snapshot) => loadSnapshot(snapshot, VIEW.CONFIRM)

    const handleRestore = async () => {
        setIsBusy(true)
        try {
            const result = await ApiActions.restoreSnapshot({ teleprojectId: teleproject, version: selected.version, publish })

            const skipped = (result && result.skipped) || []
            toast(skipped.length > 0
                ? `Restored ${(result.restored || []).join(', ')}. Not restored: ${skipped.join(', ')}`
                : 'Snapshot restored')

            //The builder loads its state once on mount and offers no refetch, so
            //the canvas would still be showing the pre-restore script - and the
            //next node save would write it straight back over what we restored.
            setTimeout(() => window.location.reload(), 1500)
        } catch (error) {
            toast('Could not restore this snapshot')
            setIsBusy(false)
        }
    }

    const currentLanguages = ((teleprojectdata && teleprojectdata.languages) || []).map(language => language.name)
    const snapshotLanguages = (selectedScripts || []).map(script => script.scriptLanguage)
    const missingHere = snapshotLanguages.filter(language => currentLanguages.indexOf(language) === -1)
    const notInSnapshot = currentLanguages.filter(language => snapshotLanguages.indexOf(language) === -1)

    const renderList = () => (
        <div className="snapshot-capture">
            <FormInput
                type="text"
                label="Snapshot name"
                placeholder="Name this version so you can recognise it later"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
            />
            {/* A name is required: the label is baked into the immutable object
                key, so an unnamed snapshot can never be named afterwards and the
                list becomes a column of timestamps. */}
            <FutworkButton buttonStyle="primary--solid" buttonSize="sm"
                disabled={isBusy || !label.trim()} onClick={handleCapture}>
                Capture snapshot
            </FutworkButton>

            <p className="snapshot-note">
                A snapshot stores the script as it was last <b>saved</b>.
                {unSaved ? ' You have unsaved changes on the canvas - they will not be included.' : ''}
            </p>

            {isLoading ? <ThreeDotLoader /> : (
                snapshots.length === 0
                    ? <p className="snapshot-empty">No snapshots yet for this project.</p>
                    : <ul className="snapshot-list">
                        {snapshots.map(snapshot => (
                            <li key={snapshot.version}>
                                <div>
                                    <div className="snapshot-label">{displayLabel(snapshot)}</div>
                                    <div className="snapshot-meta">{formatCapturedAt(snapshot.capturedAt)}</div>
                                </div>
                                <div className="snapshot-row-actions">
                                    <FutworkButton buttonStyle="primary--outline" buttonSize="sm"
                                        disabled={isBusy} onClick={() => handlePreview(snapshot)}>
                                        Preview
                                    </FutworkButton>
                                    <FutworkButton buttonStyle="primary--outline" buttonSize="sm"
                                        disabled={isBusy} onClick={() => openFlowInNewTab(snapshot)}>
                                        Open flow
                                    </FutworkButton>
                                    <FutworkButton buttonStyle="warning--solid" buttonSize="sm"
                                        disabled={isBusy} onClick={() => openConfirm(snapshot)}>
                                        Restore
                                    </FutworkButton>
                                </div>
                            </li>
                        ))}
                    </ul>
            )}
        </div>
    )

    const renderPreview = () => (
        <div className="snapshot-preview">
            <div className="snapshot-preview-head">
                <span>Snapshot name : {displayLabel(selected)}</span>
                <span className="snapshot-meta">Snapshot captured on : {formatCapturedAt(selected.capturedAt)}</span>
            </div>

            <PreviewInteractiveScript
                allScripts={selectedScripts}
                projectTitle={teleprojectTitle ? teleprojectTitle.toString() : ''}
                faqs={selectedScripts && selectedScripts[0] ? selectedScripts[0].faqs : []}
                teleprojectdata={teleprojectdata}
            />
        </div>
    )

    const renderConfirm = () => (
        <div className="snapshot-confirm">
            {/* "this script" is ambiguous on a screen showing two of them, so the
                snapshot and the live script are each named explicitly. */}
            <p className='snapshot-note'>
                Restore the snapshot : <b>{displayLabel(selected)}</b>
            </p>
            <p className='snapshot-note'>
                Captured on : {formatCapturedAt(selected.capturedAt)}
            </p>

            {/* One warning block, above the choice: the script being replaced is
                not kept anywhere, and that is the consequence most worth reading
                before the decision rather than after it. */}
            <div className="snapshot-warning">
                {scriptPublished
                    ? <p>The script currently running for this project is <b>live</b> on the app.</p>
                    : <p>The script currently running for this project is not live, so callers are unaffected.</p>}
                <p>
                    Restoring <b>replaces it permanently</b> &mdash; it is not kept anywhere
                    unless you capture it first. Go back to capture it before restoring.
                </p>
            </div>

            <div className="snapshot-choice">
                <label className={publish === false ? 'selected' : ''}>
                    <input type="radio" name="snapshot-publish" checked={publish === false}
                        onChange={() => setPublish(false)} />
                    <div>
                        <b>Restore without publishing</b>
                        <span>The script is taken off the app until someone publishes it.</span>
                    </div>
                </label>

                <label className={publish === true ? 'selected' : ''}>
                    <input type="radio" name="snapshot-publish" checked={publish === true}
                        onChange={() => setPublish(true)} />
                    <div>
                        <b>Restore &amp; publish</b>
                        <span>Callers see the restored script straight away.</span>
                    </div>
                </label>
            </div>

            <p className="snapshot-note">
                Languages in this snapshot: {snapshotLanguages.join(', ') || 'none'}
            </p>
            {notInSnapshot.length > 0 &&
                <p className="snapshot-note">
                    Not in this snapshot and left unchanged: {notInSnapshot.join(', ')}
                </p>}
            {missingHere.length > 0 &&
                <p className="snapshot-warning">
                    No longer in this project and will be skipped: {missingHere.join(', ')}
                </p>}

            {/* Capturing the current script is its own action and already sits at
                the top of the list view - pointing back to it keeps this screen
                to the one decision it exists for. */}
        </div>
    )

    //Opened in a new tab rather than in this popup: a 130-node graph needs the
    //full screen, and a separate tab is also the cheapest way to hold the live
    //script and a snapshot side by side.
    const openFlowInNewTab = (snapshot) => {
        const url = `/interactive-script/${teleproject}/snapshot/${encodeURIComponent(snapshot.version)}`
        const tab = window.open(url, '_blank')
        if (tab) tab.opener = null
    }

    //Actions live in the popup's own footer rather than in the body: the body is
    //the only thing that scrolls, so the simulator cannot push them off-screen.
    const renderFooter = () => {
        if (view === VIEW.PREVIEW) {
            return <>
                <FutworkButton buttonStyle="primary--outline" buttonSize="sm"
                    onClick={() => setView(VIEW.LIST)}>
                    Back
                </FutworkButton>
                <FutworkButton buttonStyle="primary--outline" buttonSize="sm"
                    onClick={() => openFlowInNewTab(selected)}>
                    Open flow
                </FutworkButton>
                <FutworkButton buttonStyle="warning--solid" buttonSize="sm"
                    onClick={() => { setConfirmFrom(VIEW.PREVIEW); setPublish(null); setView(VIEW.CONFIRM) }}>
                    Restore this snapshot
                </FutworkButton>
            </>
        }

        if (view === VIEW.CONFIRM) {
            return <>
                <FutworkButton buttonStyle="primary--outline" buttonSize="sm"
                    disabled={isBusy} onClick={() => setView(confirmFrom)}>
                    Cancel
                </FutworkButton>
                <FutworkButton buttonStyle="warning--solid" buttonSize="sm"
                    disabled={isBusy || publish === null} onClick={handleRestore}>
                    {isBusy ? 'Restoring...' : (publish ? 'Restore & publish' : 'Restore')}
                </FutworkButton>
            </>
        }

        return null
    }
    
    console.log('VIEW', view)
    return (
        <Popup
            show={show}
            closePopup={closePopup}
            size="md"
            heading="Snapshots"
            stickyHeader={true}
            footerAlign="right"
            popupFooter={renderFooter()}
        >
            {view === VIEW.LIST && renderList()}
            {view === VIEW.PREVIEW && selected && renderPreview()}
            {view === VIEW.CONFIRM && selected && renderConfirm()}
        </Popup>
    )
}

export default Snapshots
